import React, { useState, useContext, useRef } from "react";
import { AppContext } from "../../pages/direct/direct-router";
import API_CLIENT from "../../utility/api-client";
import { uuid4 } from "@sentry/utils";
import { Modal, Input, Button, Text, Spacer, useToasts } from "@geist-ui/core";
import { Upload } from "@geist-ui/icons";

interface FileUploadPopupProps {
  investmentId: string;
  onDocumentUploaded: () => void;
  trigger?: React.ReactNode;
}

const FileUploadPopup: React.FC<FileUploadPopupProps> = ({
  investmentId,
  onDocumentUploaded,
  trigger
}) => {
  const [showForm, setShowForm] = useState(false);
  const [documentName, setDocumentName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const app_context = useContext(AppContext);
  const { setToast } = useToasts();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const selectedFile = event.target.files[0];
      if (selectedFile.type !== "application/pdf") {
          setToast({ text: "Bitte wählen Sie eine PDF-Datei aus.", type: "error" });
          return;
      }
      setFile(selectedFile);
    }
  };

  const handleSubmit = async () => {
    if (!file || !documentName) return;
    setLoading(true);

    try {
      API_CLIENT.postInvestmentDoc(
        app_context.token,
        investmentId,
        file,
        uuid4() + ".pdf",
        documentName,
        () => {
            onDocumentUploaded();
            setToast({ text: "Dokument erfolgreich hochgeladen", type: "success" });
            setShowForm(false);
            setDocumentName("");
            setFile(null);
        }
      );
    } catch (error) {
      console.error("Error uploading document:", error);
      setToast({ text: "Fehler beim Hochladen", type: "error" });
    } finally {
        setLoading(false);
    }
  };

  const closeHandler = () => {
      setShowForm(false);
      setLoading(false);
  }

  return (
    <>
        <div onClick={() => setShowForm(true)} style={{ display: 'inline-block' }}>
            {trigger ? trigger : (
                 <Button auto type="secondary">Dokument beifügen</Button>
            )}
        </div>

        <Modal visible={showForm} onClose={closeHandler}>
            <Modal.Title>Dokument beifügen</Modal.Title>
            <Modal.Content>
                <div style={{ display: 'grid', gap: '16px' }}>
                    <Input 
                        width="100%" 
                        placeholder="z.B. Anlage A" 
                        value={documentName} 
                        onChange={(e) => setDocumentName(e.target.value)}
                    >
                        <Text small b>Dokumentenname</Text>
                    </Input>
                    
                    <div>
                        <Text small b style={{ display: 'block', marginBottom: '8px' }}>Datei (PDF)</Text>
                         <input
                            type="file"
                            accept="application/pdf"
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                            ref={fileInputRef}
                        />
                         <div 
                             onClick={() => fileInputRef.current?.click()}
                             style={{ 
                                 border: '1px dashed #eaeaea', 
                                 borderRadius: '6px', 
                                 padding: '16px', 
                                 textAlign: 'center', 
                                 cursor: 'pointer',
                                 background: '#fafafa',
                                 transition: 'background 0.2s'
                             }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0f0'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#fafafa'}
                        >
                            {file ? (
                                 <Text span style={{ fontWeight: 600 }}>{file.name}</Text>
                            ) : (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', color: '#666' }}>
                                    <Upload size={16} />
                                    <Text span font="13px">Datei auswählen</Text>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Modal.Content>
            <Modal.Action passive onClick={() => setShowForm(false)}>Abbrechen</Modal.Action>
            <Modal.Action loading={loading} onClick={handleSubmit}>Hochladen</Modal.Action>
        </Modal>
    </>
  );
};

export default FileUploadPopup;
